import React from 'react';
import { useState, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Alert, Link, TextField, Select, FormControl, MenuItem, Container, Box, Card, CardContent, Button, Typography, Divider, Tooltip } from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';

import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { AppContext } from "/@/app/AppContext.js";
import { useTheme } from '@mui/material/styles';
import { ExpirationNotice } from '/@/app/components/ExpirationNotice';
import { Spinner } from '/@/app/components/Spinner';
import { useApi} from '/@/app/Api';
import { formatDate } from '/@/app/utils';
import { ProfdataImportBar } from '/@/app/components/ProfdataImportBar';
import { route } from '/@/app/routes';


const Panel = ({ panel, sx }) => {
    const api=useApi();
    const labTestsRoute = route("/lab-tests/");
    const dates = `${formatDate(panel.created_at)}`;
    const theme = useTheme();


    const doOpen = () => {
        api.request(`cabinet/panels/${panel.id}/current/`,{}, { method: 'PUT' }
        ).then(data => {
            location.href = labTestsRoute;
        });
    }
    const doReset = () => {
        if (confirm("Вы выбрали сброс к анализам из лаборатории. Все данные, введённые вами вручную, будут удалены. Ответы на вопросы останутся. Вы уверены, что хотите сбросить панель?")) {
            api.request(`cabinet/panels/${panel.id}/reset-from-parent/`, {}, {method: 'PUT'}
            ).then(data => {
                location.reload();
            });
        }
    }
    const doDelete = () => {
        if (confirm("Удалить панель, все ответы на вопросы и все данные анализов? Восстановление невозможно.")) {
            api.request(`cabinet/panels/${panel.id}/`, {}, {method: 'DELETE'}
            ).then(data => {
                location.reload();
            });
        }
    }

    let bgStatus;
    if (panel.is_imported) {
        bgStatus = "#fdf4ff";
    } else if (panel.is_latest) {
        bgStatus = "#f4fbe1";
    } else if (!panel.is_editable) {
        bgStatus = "#f6f6f6";
    } else {
        bgStatus = "white";
    }
    let activeColor = "rgb(162,198,162)";

    let discountTill = null;
    if (panel.allows_discount_till) {
        discountTill = new dayjs(panel.allows_discount_till);
    }

    return (
        <Card variant="outlined" sx={{
            background: bgStatus,
            border: (panel.is_current ? `5px solid ${activeColor}` : ""),
            boxShadow: `0px 3px 10px 0px rgba(0,0,0,0.1)`,
            pb: "0!important",
            ...sx
        }}>
            <CardContent sx={{ fontSize: "16px", pb: "0!important" }}>
                <Box sx={{ m: 1, fontSize: "20px" }}>
                    {panel.is_imported ?
                        <>Данные из лаборатории от {dates}</>
                        :
                        <>Панель от {dates}</>
                    }
                </Box>
                {panel.derived_from &&
                    <>
                        <Box sx={{ fontSize: "12px" }}>На основе данных лаборатории {panel.derived_from.import_source}, заявка {panel.derived_from.import_identifier}</Box>
                        {panel.discount_applied ?
                            <Box sx={{color: "green" }}>скидка использована</Box>
                            :
                            <Box>
                                {panel.discount_active ?
                                    <Link href={route("/subscriptions/")} sx={{color: "blue", fontSize: "20px" }}>используйте скидку до {formatDate(discountTill)}</Link>
                                    :
                                    <Box sx={{color: "darkred"}}>скидка истекла</Box>
                                }
                            </Box>
                        }
                    </>
                }
                <Box sx={{ m: 1 }}>
                    {!panel.is_current ?
                        <Button variant="outlined" color="secondary" onClick={doOpen}>
                            <LaunchIcon sx={{ pr: 1 }}/> Открыть панель
                        </Button>
                        :
                        <Box>
                            <Box sx={{ color: activeColor, fontWeight: "bold", fontSize: "24px" }}>
                                Панель сейчас открыта
                            </Box>

                            <Link component={ReactRouterLink} to={route("/lab-tests/")}
                                  sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 1 }}
                            >
                                Перейти
                                <ArrowForwardIcon sx={{ pr: 1 }} />
                            </Link>
                        </Box>
                    }
                </Box>

                <Box sx={{ m: 1, mt: 2, fontSize: "12px", display: "flex", flexFlow: "row wrap", justifyContent: "center", gap: "20px", color: theme.palette.secondary.main }}>
                    {/* FIXME: not showing measurement count for imported panels yet, */}
                    {panel.stats.measurements_count > 0 &&
                        <Box>
                            {panel.stats.measurements_count} измерений
                        </Box>
                    }
                    {/* just because it differs from copied, which and raises questions. */}
                    {!panel.is_imported &&
                        <>
                            {panel.stats.answers_count > 0 &&
                                <Box>
                                    {panel.stats.answers_count} ответов на вопросы
                                </Box>
                            }
                            {panel.stats.risks_count > 0 &&
                                <Box>
                                    {panel.stats.risks_count} рисков
                                </Box>
                            }
                            {panel.stats.plan_steps_count > 0 &&
                                <Box>
                                    {panel.stats.plan_steps_count} шагов плана
                                </Box>
                            }
                        </>
                    }
                </Box>

                {panel.is_imported ?
                    <Box sx={{ fontWeight: "bold" }}>
                        лаборатория {panel.import_source}, заявка {panel.import_identifier}
                    </Box>
                    :
                    !panel.is_editable &&
                    <Box sx={{ fontWeight: "bold" }}>
                        Архивная: прошло больше 180 дней.
                    </Box>
                }

                {panel.is_editable &&
                    <>
                        <Box sx={{ display: "flex", flexFlow: "row wrap", gap: "10px", m: 1, justifyContent: "flex-end", flexGrow: 0 }}>
                            {panel.derived_from &&
                                <Box>
                                    <Tooltip title="Сбросить к анализам из лаборатории">
                                        <Button
                                            variant="text"
                                            size="small"
                                            color="warning"
                                            onClick={doReset}
                                            sx={{ minWidth: "auto" }}
                                        >
                                            <RestartAltIcon />
                                        </Button>
                                    </Tooltip>
                                </Box>
                            }

                            {!panel.derived_from ?
                                <Tooltip title="Удалить панель">
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={doDelete}
                                        sx={{ color: theme.palette.error.main, minWidth: "auto" }}
                                    >
                                        <DeleteForeverIcon />
                                    </Button>
                                </Tooltip>
                                :
                                <Tooltip title="Импортированную панель удалить невозможно. Обратитесь к администратору Nestarenie.Expert, если панель была импортирована по ошибке.">
                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{ color: "lightgrey", minWidth: "auto" }}
                                    >
                                        <DeleteForeverIcon />
                                    </Button>
                                </Tooltip>
                            }
                        </Box>
                    </>
                }
            </CardContent>
        </Card>
    )
}


export const CreateNew = () => {
    const api=useApi();
    const { user } = useContext(AppContext).bootstrapData;

    const [select, setSelect] = useState("now");
    const [panelDate, setPanelDate] = useState(null);
    const [dateError, setDateError] = useState(false);

    const doCreateNew = () => {
        if (select === "by-date" && !panelDate) {
            setDateError(true);
            return;
        }

        api.request(`cabinet/panels/`, {
                date: select === "now" ? null : dayjs(panelDate).format("YYYY-MM-DD"),
            },
	{
            method: 'POST',
        }).then(data => {
            location.reload();
        });
    }

    return (
        <Box sx={{ textAlign: "center", m: 2 }}>
            <Box sx={{ mb: 1 }}>
                {select === "by-date" &&
                    <FormControl>
                        <DatePicker
                            openTo="year"
                            views={['year', 'month', 'day']}
                            value={null}
                            label="Дата взятия анализов"
                            onChange={(newValue) => {setPanelDate(newValue); setDateError(false); } }
                            components={{
                                textField: TextField,
                            }}
                        />
                    </FormControl>
                }
            </Box>
            <Box>
                <Button variant="contained" sx={{ height: "38px", mb: 1 }} onClick={doCreateNew}>
                    Создать новую панель
                </Button>
                <FormControl>
                    <Select
                        value={select}
                        size="small"
                        onChange={(event) => setSelect(event.target.value) }
                        sx={{ ml: 2 }}
                        inputProps={{ 'aria-label': 'Without label' }}
                    >
                        <MenuItem value="now">За сегодня</MenuItem>
                        <MenuItem value="by-date">За выбранную дату</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}


export const PanelsView = () => {
    const api=useApi();
    const [loading, setLoading] = useState(true);
    const [panels, setPanels] = useState(null);

    // Fetch on mount
    useEffect(() => {
        api.request('cabinet/panels/')
            .then(data => {
                setLoading(false);
                setPanels(data.filter(item => !item.is_imported));  // don't show imported
            })
            .catch(error => {
                setLoading(false);
            });
    }, []);

    return (
        <Container maxWidth="sm">
            <Box sx={{ pr: 2 }}>
                <ExpirationNotice />
            </Box>
            <Typography variant="h3" component="h1" gutterBottom>
                <AutoAwesomeMotionOutlinedIcon sx={{ fontSize: "35px", mr: 2 }} />
                Ваши панели
            </Typography>

            {loading ?
                <Box sx={{ textAlign: "center", m: 2 }}>
                    <Spinner delay={500} />
                </Box>
                :
                <Box sx={{ textAlign: "center" }}>
                    <ProfdataImportBar />
                    <Divider sx={{ m: 2 }}/>
                    <Typography variant="h6" component="h4" gutterBottom sx={{ textAlign: "center" }}>Все панели</Typography>
                    <CreateNew />
                    <Typography variant="h4" component="h1" gutterBottom>
                        {panels.map((item, index) => (
                            <Panel key={index} panel={item} sx={{ mb: 3 }}></Panel>
                        ))}
                    </Typography>
                </Box>
            }
        </Container>
    );
}
